import { forwardRef, useCallback, useEffect, useRef, useState, type ComponentPropsWithoutRef } from 'react'
import strings from '../locales/must-change.json'
import { useLang } from '../lib/i18n'

// R456: every staff password field gets a show/hide eye. The toggle changes ONLY the input's
// type; every other prop (id, name, autoComplete, value, onChange) passes through untouched.
// The value is never read, logged or stored here. State is component-local, so it resets on
// unmount/navigation, and it goes back to hidden when the field's own form submits (a native
// listener on input.form; no submit handler is touched).
// R537: the eye's name is in the till's language, under the switch (was both, Arabic first).
// The till has no icon library: the eye / eye-off paths are lucide's (ISC), as in Icons.tsx.
type PasswordInputProps = Omit<ComponentPropsWithoutRef<'input'>, 'type'>

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true" focusable="false" className="h-5 w-5 shrink-0">
      <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true" focusable="false" className="h-5 w-5 shrink-0">
      <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
      <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
      <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
      <path d="m2 2 20 20" />
    </svg>
  )
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
  { className, disabled, ...inputProps },
  ref,
) {
  const [shown, setShown] = useState(false)
  const lang = useLang()
  const inputRef = useRef<HTMLInputElement | null>(null)

  const setRefs = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [ref],
  )

  useEffect(() => {
    const form = inputRef.current?.form
    if (!form) return
    const hideOnSubmit = () => setShown(false)
    form.addEventListener('submit', hideOnSubmit)
    return () => form.removeEventListener('submit', hideOnSubmit)
  }, [])

  const label = strings[shown ? 'hidePassword' : 'showPassword'][lang]

  return (
    <div className="relative">
      <input
        {...inputProps}
        ref={setRefs}
        type={shown ? 'text' : 'password'}
        disabled={disabled}
        className={`${className ?? ''} pe-12`}
      />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        aria-pressed={shown}
        aria-label={label}
        title={label}
        disabled={disabled}
        data-password-toggle=""
        className="absolute end-0 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-lg text-fleet-500 transition-colors hover:text-fleet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy disabled:cursor-not-allowed disabled:opacity-60"
      >
        {shown ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  )
})
